import { type ComponentPropsWithoutRef, type PropsWithChildren, memo, useMemo } from 'react'
import { type VariantProps, tv } from 'tailwind-variants'

// HINT: trianble部分はRetinaディスプレイなどで途切れてしまう場合があるので
// 1pxほど大きめに描画してbody部分と被るようにしています。
const classNameGenerator = tv({
  base: [
    'smarthr-ui-Balloon',
    'shr-relative',
    'shr-z-1',
    'shr-inline-block',
    'shr-text-sm',
    'shr-rounded-s',
    /* drop-shadow は spread-radius を受け付けないので shadow.LAYER2 に近い値をハードコーディングしている */
    'shr-drop-shadow-[0_2px_2.5px_theme(colors.transparency.30)]',
    // Safariでdrop-shadowが残り続けてしまうバグの対応
    'shr-will-change-[filter]',
    'shr-whitespace-nowrap',
    'shr-bg-white',
    'shr-text-black',
    'after:shr-block',
    'after:shr-absolute',
    'after:shr-content-[""]',
    'after:shr-bg-white',
    'contrast-more:shr-border-shorthand',
    'contrast-more:shr-border-high-contrast',
    'contrast-more:before:shr-block',
    'contrast-more:before:shr-absolute',
    'contrast-more:before:shr-content-[""]',
    'contrast-more:before:shr-bg-black',
  ],
  variants: {
    horizontal: {
      center: [
        'before:shr-left-1/2',
        'before:-shr-translate-x-[5px]',
        'after:shr-left-1/2',
        'after:-shr-translate-x-[5px]',
      ],
      right: '',
      left: '',
    },
    vertical: {
      top: [
        'before:-shr-top-[5px]',
        'before:shr-w-[10px]',
        'before:shr-h-[5px]',
        'before:[clip-path:polygon(50%_0,100%_100%,0_100%)]',
        'after:-shr-top-0.25',
        'after:shr-w-[10px]',
        'after:shr-h-[5px]',
        'after:[clip-path:polygon(50%_0,100%_100%,0_100%)]',
      ],
      bottom: [
        'before:-shr-bottom-[5px]',
        'before:shr-w-[10px]',
        'before:shr-h-[5px]',
        'before:[clip-path:polygon(0_0,100%_0,50%_100%)]',
        'after:-shr-bottom-0.25',
        'after:shr-w-[10px]',
        'after:shr-h-[5px]',
        'after:[clip-path:polygon(0_0,100%_0,50%_100%)]',
      ],
      middle: [
        'before:shr-top-1/2',
        'before:-shr-translate-y-[5px]',
        'after:shr-top-1/2',
        'after:-shr-translate-y-[5px]',
      ],
    },
    triggerIcon: {
      true: '',
    },
  },
  compoundVariants: [
    {
      vertical: ['top', 'bottom'],
      horizontal: 'left',
      className: ['before:shr-left-1.5', 'after:shr-left-1.5'],
    },
    {
      vertical: ['top', 'bottom'],
      horizontal: 'left',
      triggerIcon: true,
      className: ['before:shr-left-0.5', 'after:shr-left-0.5'],
    },
    {
      vertical: ['top', 'bottom'],
      horizontal: 'right',
      className: ['before:shr-right-1.5', 'after:shr-right-1.5'],
    },
    {
      vertical: ['top', 'bottom'],
      horizontal: 'right',
      triggerIcon: true,
      className: ['before:shr-right-0.5', 'after:shr-right-0.5'],
    },
    {
      vertical: 'middle',
      horizontal: 'left',
      className: [
        'before:-shr-left-[5px]',
        'before:shr-w-[5px]',
        'before:shr-h-[10px]',
        'before:[clip-path:polygon(100%_0,100%_100%,0_50%)]',
        'after:-shr-left-0.25',
        'after:shr-w-[5px]',
        'after:shr-h-[10px]',
        'after:[clip-path:polygon(100%_0,100%_100%,0_50%)]',
      ],
    },
    {
      vertical: 'middle',
      horizontal: 'right',
      className: [
        'before:-shr-right-[5px]',
        'before:shr-w-[5px]',
        'before:shr-h-[10px]',
        'before:[clip-path:polygon(0_0,100%_50%,0_100%)]',
        'after:-shr-right-0.25',
        'after:shr-w-[5px]',
        'after:shr-h-[10px]',
        'after:[clip-path:polygon(0_0,100%_50%,0_100%)]',
      ],
    },
  ],
})

type AbstractProps = PropsWithChildren<
  VariantProps<typeof classNameGenerator> & {
    /** レンダリングするタグ */
    as?: 'div' | 'span'
  }
>
type Props = AbstractProps & Omit<ComponentPropsWithoutRef<'div'>, keyof AbstractProps>

export const Balloon = memo<Props>(
  ({ horizontal, vertical, triggerIcon, className, as: Component = 'div', ...rest }) => {
    const actualClassName = useMemo(
      () => classNameGenerator({ horizontal, vertical, triggerIcon, className }),
      [horizontal, vertical, className, triggerIcon],
    )

    return <Component {...rest} className={actualClassName} />
  },
)
